/**
 * TRAVEL CARD (ADR-011, P3.md 5b) — the full-screen gate/border surface.
 *
 * Renders ENTIRELY from the precomputed travel-window snapshot (db/idb.ts
 * TravelWindowLeg): no registry walk, no gig-state parse, no network, no auth
 * on this path — main.tsx paints it BEFORE app hydration. Big type, one leg,
 * following legs compact underneath. The pass area is an honest placeholder
 * until the P4 pin-time pipeline lands.
 *
 * Screen Wake Lock is held while the card is up (re-acquired on visibility
 * return — iOS drops it on background) and released on dismiss.
 */
import { useEffect, useRef } from "react";
import { t, type Locale } from "../i18n.ts";
import type { TravelWindowLeg } from "../db/idb.ts";
import { PlaneIcon, WheelsIcon } from "../components/icons.tsx";
import { PassSlot } from "../pass/PassSlot.tsx";

/** Minimal Wake Lock surface (lib.dom's, kept structural for test doubles). */
interface WakeLockSentinelLike {
  release(): Promise<void>;
}
interface WakeLockLike {
  request(type: "screen"): Promise<WakeLockSentinelLike>;
}

function useScreenWakeLock(active: boolean): void {
  const sentinel = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    if (!active) return;
    const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
    if (wakeLock === undefined) return; // older iOS — the card still renders

    let disposed = false;
    const acquire = async () => {
      try {
        const s = await wakeLock.request("screen");
        if (disposed) void s.release();
        else sentinel.current = s;
      } catch {
        /* low battery / not visible — never let the lock break the card */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
    };
  }, [active]);
}

function place(name: string, iata: string): string {
  if (iata !== "") return name !== "" ? `${name} (${iata.toUpperCase()})` : iata.toUpperCase();
  return name || "—";
}

function offsetChip(locale: Locale, n: number): string | null {
  if (n > 0) return t(locale, "day_run.plus_days", { n });
  if (n < 0) return t(locale, "day_run.minus_days", { n: -n });
  return null;
}

export interface TravelCardProps {
  locale: Locale;
  /** Legs currently inside their window, soonest departure first. */
  legs: readonly TravelWindowLeg[];
  gigName: string;
  /** Dismiss into the (already hydrating) app behind the card. */
  onDismiss(): void;
}

export function TravelCard({ locale, legs, gigName, onDismiss }: TravelCardProps) {
  useScreenWakeLock(true);
  const leg = legs[0];
  if (leg === undefined) return null;

  const rest = legs.slice(1, 3);
  // The slot row: seat · gate · terminal (flights) / carriage · platform (rail).
  const slots: { label: string; value: string }[] = [
    { label: t(locale, "travel_card.seat"), value: leg.seat },
    { label: t(locale, "travel_card.gate"), value: leg.gate },
    { label: t(locale, "travel_card.terminal"), value: leg.terminal },
    { label: t(locale, "travel_card.carriage"), value: leg.carriage },
    { label: t(locale, "travel_card.platform"), value: leg.platform },
  ].filter((s) => s.value !== "");
  const arrChip = offsetChip(locale, leg.day_offset);

  return (
    <div className="travel-card" role="dialog" aria-label={t(locale, "travel_card.title")}>
      <header className="travel-card-top">
        <span className="travel-card-kicker">
          {leg.kind === "flight" ? <PlaneIcon size={18} /> : <WheelsIcon size={18} />}
          {[leg.carrier, leg.ref].filter(Boolean).join(" ") || gigName}
        </span>
      </header>

      <div className="travel-card-route">
        <div className="travel-card-end">
          <span className="travel-card-place">{place(leg.dep_place, leg.dep_iata)}</span>
          <span className="travel-card-time num">{leg.dep_time || "—"}</span>
          <span className="travel-card-day num">{leg.dep_day}</span>
        </div>
        <span className="travel-card-arrow" aria-hidden="true">
          →
        </span>
        <div className="travel-card-end">
          <span className="travel-card-place">{place(leg.arr_place, leg.arr_iata)}</span>
          <span className="travel-card-time num">
            {leg.arr_time || "—"}
            {arrChip !== null && <sup className="travel-card-offset">{arrChip}</sup>}
          </span>
          <span className="travel-card-day num">{leg.arr_day}</span>
        </div>
      </div>

      {slots.length > 0 && (
        <dl className="travel-card-slots">
          {slots.map((s) => (
            <div key={s.label} className="travel-card-slot">
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* ADR-011: the pass loads AFTER the card paints — the fast lane never
          waits on IndexedDB blob reads. Pinned = barcode + raster at full
          brightness; unpinned = the one pin action. */}
      <PassSlot locale={locale} gigId={leg.gig_id} travelItemId={leg.item_id} personId={leg.person_id} />

      {rest.length > 0 && (
        <ul className="travel-card-rest">
          {rest.map((l) => (
            <li key={`${l.gig_id}:${l.item_id}`}>
              <span className="num">{l.dep_time || "·"}</span>
              <span>
                {place(l.dep_place, l.dep_iata)} → {place(l.arr_place, l.arr_iata)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="travel-card-dismiss" onClick={onDismiss}>
        {t(locale, "travel_card.open_app")}
      </button>
    </div>
  );
}
